// @ts-ignore
import React, { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { routes } from "../../../appRoutingHelpers";
import { GoBackLinkButton } from "../../../components/GoBackLinkButton";
import {
  ChallengeFormType,
  ChallengeType,
  ObjectiveType,
} from "../../../service/api-response/Challenges";
import * as authSelectors from "../../auth/authSelectors";
import {
  useChallenge,
  useCreateChallenge,
  useGetCountries,
  useUpdateChallenge,
  useUploadImage,
} from "../../../service/api";
import moment from "moment";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { SpinnerBlock } from "../../common/Spinner";
import { StickyBottomBar } from "../../../components/StickyBottomBar";
import { ChallengeGeneralInfoSection } from "./ChallengeGeneralInfoSection";
import { ChallengeImageSection } from "./ChallengeImageSection";
import { ChallengeValiditySection } from "./ChallengeValiditySection";
import { ChallengeObjectiveSection } from "./ChallengeObjectiveSection";
import { ChallengeRewardConfigSection } from "./ChallengeRewardConfigSection";
import { toastr } from "react-redux-toastr";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import {
  transformPriceInput,
  validateAboveZero,
} from "../../../components/forms/inputs/PriceInput";
import { getCountrySpecificValues, transformPriceFromAPI } from "../../../util";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";
import { ButtonAction } from "../../../components/NewButtons";

const challengeSchema = yup.object().shape({
  name: yup.string().required("Please enter admin name to continue"),
  sortKey: yup.string().required("Please enter sort key to continue"),
  activeFrom: yup
    .string()
    .test(
      "active to > active from test",
      "This date has to be before the last day the challenge is visible for the user",
      (value, ctx) => {
        const isBeforeExpiryDate = moment(ctx.parent.activeExpiresAt).isAfter(
          moment(value),
          "day"
        );
        return isBeforeExpiryDate;
      }
    )
    .required("Please enter active from to continue"),
  recurring: yup
    .boolean()
    .required("Please choose if challenge is recurring to continue"),
  enabled: yup
    .boolean()
    .required("Please choose if challenge is enabled to continue"),
  autoClaimReward: yup
    .boolean()
    .required("Please choose if reward is automatically claimed to continue"),
  separateCompletionDate: yup.boolean().optional(),
  title: yup.string().required("Please enter challenge name to continue"),
  description: yup.string().required("Please enter description to continue"),
  imageUrl: yup.string().when("$isEdit", (isEdit, schema) => {
    if (isEdit) {
      return schema.required("Please add an image to continue");
    }
    return;
  }),
  activeExpiresAt: yup.string().required("Please enter active to to continue"),
  completedExpiresAt: yup
    .string()
    .test(
      "completed > active to test",
      "This date has to be after the last day the challenge is visible",
      (value, ctx) => {
        const isAfterExpiryDate = moment(ctx.parent.activeExpiresAt).isBefore(
          moment(value),
          "day"
        );
        return isAfterExpiryDate;
      }
    )
    .required("Please enter last completion date to continue"),
  objectiveType: yup
    .string()
    .required("Please choose objective type to continue"),
  objectiveGoal: yup
    .string()
    .when("objectiveType", (objectiveType) => {
      if (objectiveType === ObjectiveType.ORDER_CREATED) {
        return yup.number().min(1, "Please enter a number above 0");
      }
      return validateAboveZero("currency");
    })
    .required("Please enter objective goal to continue"),
  objectiveProductId: yup.string().when("objectiveType", (objectiveType) => {
    if (objectiveType === ObjectiveType.ORDER_DELIVERED_PRODUCT_COUNT) {
      return yup.string().required("Please select a product to continue");
    }
    return yup.string().optional();
  }),
  currency: yup.string().when("objectiveType", (objectiveType) => {
    if (objectiveType !== ObjectiveType.ORDER_DELIVERED_NO_GIFT_CARD) {
      return yup.string().optional();
    }
    return yup.string().required("Please choose currency to continue");
  }),
  countryCode: yup.string().required("Please choose country to continue"),
  rewardProductId: yup
    .string()
    .required(
      "Please choose the voucher the user receives upon completion of the challenge"
    ),
  files: yup.array().when("$isEdit", (isEdit, schema) => {
    if (!isEdit) {
      return schema.min(1, "Please add an image to continue");
    }
    return;
  }),
  hasPrerequisite: yup.boolean().required(),
  prerequisiteChallengeId: yup.string().when("hasPrerequisite", {
    is: true,
    then: yup
      .string()
      .required(
        "Please choose the challenge the user has to complete to start this challenge"
      ),
  }),
});

export const ChallengesUpsert = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const isEdit = !!id;
  const { currency: defaultCurrency, countryCode } = getCountrySpecificValues();
  const selectedOrganizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const navigate = useNavigate();

  const methods = useForm<ChallengeFormType>({
    resolver: yupResolver(challengeSchema),
    defaultValues: {
      objectiveType: ObjectiveType.ORDER_DELIVERED_NO_GIFT_CARD,
      enabled: true,
      currency: defaultCurrency,
      countryCode,
      autoClaimReward: false,
      hasPrerequisite: false,
      objectiveProductId: "",
      files: [],
    },
    context: {
      isEdit: isEdit,
    },
  });

  const { handleSubmit, reset } = methods;

  const {
    mutateAsync: createChallenge,
    isLoading: creationIsLoading,
  } = useCreateChallenge();

  const {
    mutateAsync: updateChallenge,
    isLoading: updateIsLoading,
  } = useUpdateChallenge();

  const {
    mutateAsync: uploadImage,
    isLoading: imageUploadIsLoading,
  } = useUploadImage();

  const {
    data: challenge,
    isLoading: challengeIsLoading,
    error: getChallengeError,
  } = useChallenge(selectedOrganizationKeyName, id);

  const {
    data: countries,
    isLoading: countriesAreLoading,
    error: listCountriesError,
  } = useGetCountries();

  const isLoading =
    creationIsLoading ||
    updateIsLoading ||
    imageUploadIsLoading ||
    countriesAreLoading;
  const buttonText = isEdit ? "Save changes" : "Create challenge";

  useEffect(() => {
    if (isEdit && challenge) {
      const {
        activeExpiresAt,
        activeFrom,
        completedExpiresAt,
        prerequisiteChallengeId,
        objectiveGoal,
        objectiveType,
        ...rest
      } = challenge;
      let transformedObjectiveGoal = objectiveGoal;
      if (
        objectiveType === ObjectiveType.ORDER_DELIVERED_NO_GIFT_CARD ||
        objectiveType === ObjectiveType.ORDER_DELIVERED_FIRST_TIME
      ) {
        transformedObjectiveGoal = transformPriceFromAPI(
          transformedObjectiveGoal,
          rest.currency
        );
      }
      reset({
        activeExpiresAt: moment(activeExpiresAt).format("YYYY-MM-DDTHH:MM"),
        activeFrom: moment(activeFrom).format("YYYY-MM-DDTHH:MM"),
        completedExpiresAt: moment(completedExpiresAt).format(
          "YYYY-MM-DDTHH:MM"
        ),
        hasPrerequisite: prerequisiteChallengeId ? true : false,
        prerequisiteChallengeId: prerequisiteChallengeId,
        objectiveType,
        objectiveGoal: transformedObjectiveGoal,
        ...rest,
      });
    }
  }, [isEdit, challenge, reset]);

  const onSubmit = async (challenge: ChallengeFormType) => {
    const {
      activeExpiresAt,
      activeFrom,
      completedExpiresAt,
      hasPrerequisite,
      prerequisiteChallengeId,
      imageUrl,
      files,
      objectiveGoal,
      objectiveType,
      ...body
    } = challenge;
    const newChallenge: ChallengeType = {
      ...body,
      objectiveType,
      objectiveGoal,
      imageUrl,
      completedExpiresAt: moment(completedExpiresAt).format(),
      activeFrom: moment(activeFrom).format(),
      activeExpiresAt: moment(activeExpiresAt).format(),
      organizationKeyName: selectedOrganizationKeyName,
    };
    if (hasPrerequisite) {
      newChallenge.prerequisiteChallengeId = prerequisiteChallengeId;
    } else {
      /* Create fails if id is "", but to remove prerequisite challenge we need "" */
      newChallenge.prerequisiteChallengeId = isEdit ? "" : null;
    }

    if (!newChallenge.objectiveProductId) {
      newChallenge.objectiveProductId = null;
    }

    if (
      (challenge.objectiveType === ObjectiveType.ORDER_DELIVERED_NO_GIFT_CARD ||
        challenge.objectiveType === ObjectiveType.ORDER_DELIVERED_FIRST_TIME) &&
      challenge.currency
    ) {
      newChallenge.objectiveGoal = transformPriceInput(
        challenge.objectiveGoal,
        challenge.currency
      );
    }

    try {
      if (isEdit) {
        await updateChallenge({
          ...newChallenge,
          id,
        });
      } else {
        const challengeId = await createChallenge(newChallenge);
        if (files && files.length > 0 && challengeId) {
          const file = files[0];
          const imageUrl = await uploadImage({
            file,
            id: challengeId,
            organizationKeyName: selectedOrganizationKeyName,
          });
          newChallenge.imageUrl = imageUrl;
          await updateChallenge({
            ...newChallenge,
            id: challengeId,
          });
        }
      }
      toastr.success(
        "Success",
        `You succesfully ${isEdit ? "updated" : "created"} the challenge`
      );
      navigate(routes.challenges(selectedOrganizationKeyName));
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  useHandleFetchError(getChallengeError);
  useHandleFetchError(listCountriesError);

  if (isEdit && challengeIsLoading) {
    return <SpinnerBlock />;
  }

  return (
    /* @ts-ignore */
    <FormProvider {...methods}>
      <form className="col-12 px-0">
        <div className="container mt-5">
          <GoBackLinkButton
            to={routes.challenges(selectedOrganizationKeyName)}
            text="Back to all challenges"
          />
          <div className="row">
            <div className="col-12">
              <ChallengeGeneralInfoSection
                isEdit={isEdit}
                countries={countries}
              />
              <ChallengeImageSection isEdit={isEdit} />
              <ChallengeValiditySection />
              <ChallengeObjectiveSection countries={countries} />
              <ChallengeRewardConfigSection />
            </div>
          </div>
        </div>
        <StickyBottomBar>
          <div className="container d-flex py-3 justify-content-end">
            <ButtonAction
              disabled={isLoading}
              type="button"
              onClick={handleSubmit(onSubmit)}
            >
              {isLoading ? (
                <FontAwesomeIcon icon={solid("spinner")} pulse spin />
              ) : (
                buttonText
              )}
            </ButtonAction>
          </div>
        </StickyBottomBar>
      </form>
    </FormProvider>
  );
};
